'use client';

import { useEffect, useReducer } from 'react';

/**
 * Custom hook that combines useReducer with localStorage persistence
 * @template S - State type
 * @template A - Action type
 * @param {function(S, A): S} reducer - The reducer function
 * @param {S} initialState - The initial state
 * @param {string} storageKey - Key to use for localStorage
 * @returns {[S, function(A): void]} - State and dispatch function
 */
export function usePersistentReducer(reducer, initialState, storageKey) {
  // Initialize state from localStorage if available
  const [state, dispatch] = useReducer(reducer, initialState, (initial) => {
    if (globalThis.window === undefined) return initial;

    try {
      const storedValue = localStorage.getItem(storageKey);
      if (!storedValue) return initial;

      const parsedValue = JSON.parse(storedValue);

      // Validate stored state has all required fields
      const isValid = Object.keys(initial).every(
        (key) => Object.prototype.hasOwnProperty.call(parsedValue, key) && typeof parsedValue[key] === typeof initial[key]
      );

      return isValid ? parsedValue : initial;
    } catch (error) {
      console.warn(`Error loading state from localStorage (${storageKey}):`, error);
      return initial;
    }
  });

  // Update localStorage when state changes
  useEffect(() => {
    if (globalThis.window === undefined) return;

    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.warn(`Error saving state to localStorage (${storageKey}):`, error);
    }
  }, [state, storageKey]);

  return [state, dispatch];
}

/**
 * Custom hook that combines useState with localStorage persistence
 * @template T
 * @param {T} initialValue - Initial value
 * @param {string} storageKey - Key to use for localStorage
 * @returns {[T, function(T): void]} - Value and setter function
 */
export function usePersistentState(initialValue, storageKey) {
  return usePersistentReducer((_, newValue) => newValue, initialValue, storageKey);
}

/**
 * Custom hook for managing a persistent boolean toggle
 * @param {boolean} initialValue - Initial toggle state
 * @param {string} storageKey - Key to use for localStorage
 * @returns {[boolean, function(): void]} - Toggle state and toggle function
 */
export function usePersistentToggle(initialValue, storageKey) {
  const [value, setValue] = usePersistentState(initialValue, storageKey);
  const toggle = () => setValue(!value);
  return [value, toggle];
}

/**
 * Custom hook for managing a persistent array
 * @template T
 * @param {T[]} initialValue - Initial array
 * @param {string} storageKey - Key to use for localStorage
 * @returns {[T[], Object]} - Array and operations object
 */
export function usePersistentArray(initialValue, storageKey) {
  const [array, setArray] = usePersistentState(initialValue, storageKey);

  const operations = {
    add: (item) => setArray([...array, item]),
    remove: (index) => setArray(array.filter((_, i) => i !== index)),
    update: (index, item) => setArray(array.map((old, i) => (i === index ? item : old))),
    clear: () => setArray([]),
    set: setArray,
  };

  return [array, operations];
}

/**
 * Custom hook for managing a persistent object
 * @template T
 * @param {T} initialValue - Initial object
 * @param {string} storageKey - Key to use for localStorage
 * @returns {[T, Object]} - Object and operations object
 */
export function usePersistentObject(initialValue, storageKey) {
  const [object, setObject] = usePersistentState(initialValue, storageKey);

  const operations = {
    update: (updates) => setObject({ ...object, ...updates }),
    set: (key, value) => setObject({ ...object, [key]: value }),
    remove: (key) => {
      // eslint-disable-next-line no-unused-vars
      const { [key]: _, ...rest } = object;
      setObject(rest);
    },
    clear: () => setObject({}),
    reset: () => setObject(initialValue),
  };

  return [object, operations];
}
